/*
 * Copyright (c) 2023 Pegasystems Inc.
 * All rights reserved.
 */
// @ts-nocheck
import configureStore from '../store';
import * as StateManager from '../store/state/state-manager';
import * as ActionCreators from '../store/state/actions/action-creators';
import { updateViewStore, updateNavigationStore } from '../rules/rules-store';
import * as RoutingManager from '../router/routing-manager';
import AssetLoader from '../utils/asset-loader';
import loadComponents from '../utils/component-loader';
import services from '../services';
import * as StreamObservable from '../observables/stream-observable';
import ComponentsRegistry from '../components-registry';
import ActionProcessor from '../actions/processor';
import actionsHandler from '../actions/handler';
import { createC11nEnv as createC11nEnvInstance } from '../interpreter/c11n-env';
import * as ContextUtils from '../utils/context-utils';
import fieldDefaultUtils from '../fields/field-default';
import {
  init,
  publishForBrowserBookmark,
  isSemanticURL,
  getRestEndPoint,
  initializeApplicationLabel,
  getViewRestEndPoint,
  enableAppRouting as toggleAppRouting
} from '../router/app-router';
import TargetMap from '../actions/targetmap';
import { updateHeaders, fetch as serviceBrokerFetch, updateAuthInfo } from '../service_broker/fetch';
import { getTranslatedDXAPIResponse } from '../utils/dxapi-response-translator';
import Utils from '../utils/utils';
import Constants, { publicConstants } from '../constants';
import PubSubUtils from '../utils/pubsub-utils';
import CaseApis from '../case/apis';
import MashupApis from '../mashup/apis';
import { processViewResponse } from '../store/state/epics/util';
import {
  addRootContainerItem,
  removeContainer as removeContainerUtil,
  getContainerAPI,
  getActiveContainerItemName
} from '../container/container-utils';

class BootstrapUtils {
  createC11nEnv = (meta) => {
    return createC11nEnvInstance(meta);
  };

  getContextUtils = () => {
    return ContextUtils;
  };

  getRoutingManager = () => {
    return RoutingManager;
  };

  getComponentsRegistry = () => {
    return ComponentsRegistry;
  };

  getStateManager = () => {
    return StateManager;
  };

  getActionCreators = () => {
    return ActionCreators;
  };

  getStreamObservable = () => {
    return StreamObservable;
  };

  getActionProcessor = () => {
    return ActionProcessor;
  };

  getActionHandler = () => {
    return actionsHandler;
  };

  renderApp = (translatedDXAPIResponse, domContainerID) => {
    const options = { context: Constants.APP.APP };
    const rootData = Utils.getIn(translatedDXAPIResponse, Constants.ROOT);
    const root = { ...rootData, options };

    configureStore(translatedDXAPIResponse);
    StreamObservable.publishResponse(translatedDXAPIResponse, options, true);
    const props = this.createC11nEnv({
      meta: root,
      isRoot: true
    });
    /* pub sub */
    PubSubUtils.publish(publicConstants.PUB_SUB_EVENTS.EVENT_RENDER_APP, { props, domContainerID });
  };

  /** updates store with apiResponse
   * @public
   * @param {*} dxAPIResponse - uiResources response
   * @param {string} containerName - container name
   * @param {boolean} isPortal - is portal
   */
  updateStoreWithUiRoot = async (dxAPIResponse, containerName, isPortal) => {
    const { data, uiResources = {} } = dxAPIResponse;
    const { root } = uiResources;
    const contextData = Utils.getIn(uiResources, Constants.CONTEXT_DATA);
    const translatedDXAPIResponse = getTranslatedDXAPIResponse(dxAPIResponse);
    await processViewResponse(translatedDXAPIResponse, {}, {}, false, true);
    const appData = { ...data, [Constants.CONTEXT_DATA]: contextData };
    addRootContainerItem(root, containerName, appData, isPortal);
  };

  fetchEndPointResource = (endPoint, containerName, isPortal) => {
    serviceBrokerFetch(endPoint, { method: 'GET' }, Constants.APP.ROOT).then((response) => {
      if (response.status === 200) {
        this.updateStoreWithUiRoot(response.data, containerName, isPortal);
      }
    });
  };

  loadComponent = async (componentConfig, domContainerID, additionalComponents) => {
    await loadComponents(additionalComponents);
    const props = this.createC11nEnv({
      meta: componentConfig,
      isRoot: true
    });

    if (!StateManager.getStore()) {
      configureStore({});
    }

    /* pub sub */
    PubSubUtils.publish(publicConstants.PUB_SUB_EVENTS.EVENT_RENDER_APP, { props, domContainerID });
  };

  setStaticServerUrl = (staticUrl, appStaticUrl, b2sJWT) => {
    AssetLoader.initServer(staticUrl, appStaticUrl, b2sJWT);
  };

  loadView = async (dxAPIResponse, domContainerID, additionalComponents = []) => {
    services.init();
    const translatedDXAPIResponse = getTranslatedDXAPIResponse(dxAPIResponse);
    const views = Utils.getIn(translatedDXAPIResponse, Constants.VIEWS);
    const navigations = Utils.getIn(translatedDXAPIResponse, Constants.NAVIGATIONS);
    const components = Utils.getIn(translatedDXAPIResponse, Constants.COMPONENTS) || [];
    components.push(...additionalComponents);

    const staticServer = Utils.getIn(translatedDXAPIResponse, Constants.STATIC_SERVER_URL);
    const applicationData = Utils.getIn(translatedDXAPIResponse, Constants.APPLICATION_DATA);

    if (views) {
      updateViewStore(views);
    }
    if (navigations) {
      updateNavigationStore(navigations);
    }
    initializeApplicationLabel(applicationData);

    if (staticServer) {
      AssetLoader.initServer(staticServer);
    }

    await loadComponents(components, staticServer);
    this.renderApp(translatedDXAPIResponse, domContainerID);
  };

  /**
   * This is common method to initialize app shell and it gets invoked from pxBootstrapShell
   * @param {*} appRoutingTable - appRoutingTable
   * @param {*} actionsTargetModel -  actionsTargetModel
   * @param {*} servicesObj - servicesObj
   */
  initAppShell = (appRoutingTable, actionsTargetModel, servicesObj) => {
    init(appRoutingTable);
    TargetMap.buildActionTargetMap(actionsTargetModel);
    services.init(servicesObj);
  };

  /**
   * This method on the bootstrap is used to initialize just the routing table - this is used in scenarios such as the constellation
   * bridge wherein actions targeting and service objects are not useful
   * @param {*} appRoutingTable - appRoutingTable
   *  @param {*} servicesObj - servicesObj
   */
  initRoutingTable = (appRoutingTable, servicesObj) => {
    init(appRoutingTable, true);
    services.init(servicesObj);
  };

  setAppAlias = (appAlias) => {
    AssetLoader.setAppAlias(appAlias);
  };

  getHomeRestApi = (portalName) => {
    const serverURL = services.getServerURL();
    const { restApi } = getRestEndPoint('home', { portalName });

    return `${serverURL}${restApi}`;
  };

  /**
   * Returns the full path to the view endpoint
   * @param {string} viewID - View Name passed in the queryparam
   * @param {string} viewClass - View Name passed in the queryparam
   * @returns {string} Full path to the view endpoint
   */
  getViewRestApi = (viewID, viewClass) => {
    const { restApi } = getViewRestEndPoint(viewID, viewClass);
    return services.getServerURL() + restApi;
  };

  /**
   * Renders RootContainer initially
   * @param {*} domContainerID - Container under which view must be loaded
   * @param {*} componentMetaData - componentMetaData of RootContainer
   * @param {*} additionalComponents - Additional components
   * @param {*} [portalTarget] - (optional), DOM element into which view will be loaded and used as cosmos portal target(usually a shadowdom element)
   * @param {*} [styleSheetTarget] - (optional), DOM element into which cosmos styles will be injected
   * @param {boolean} [removeContainer] - check to remove the container
   */
  loadRootComponent = async (
    domContainerID: string,
    componentMetaData: { [key: string]: any },
    additionalComponents?: string[] = [],
    portalTarget?: string,
    styleSheetTarget?: string,
    removeContainer?: boolean
  ) => {
    const { config } = componentMetaData || {};
    const rootContainerName = config?.name;

    if (!StateManager.getStore()) {
      configureStore({});
    }

    if (removeContainer) {
      const containerPath = `${Constants.APP.ROOT}/${rootContainerName}`;
      removeContainerUtil(containerPath);
      getContainerAPI().deleteContainerItem(containerPath);
    }

    const containerItems = getContainerAPI().getContainerItems() || [];
    if (
      containerItems.length > 1 &&
      !rootContainerName?.startsWith('hybrid_') &&
      rootContainerName !== 'authoring-preview'
    ) {
      StateManager.resetStore();
    }

    StateManager.dispatch(
      ActionCreators.mergeData({
        data: {},
        context: Constants.APP.ROOT
      })
    );

    const props = this.createC11nEnv({
      meta: componentMetaData,
      options: {
        context: Constants.APP.ROOT,
        pageReference: '',
        target: `${Constants.APP.ROOT}/${rootContainerName}`
      }
    });

    const pConn = props.getPConnect();
    const rootContainerTarget = `${pConn.getContextName()}/${pConn.getContainerName()}`;

    if (!getActiveContainerItemName(rootContainerTarget) && config?.renderingMode !== 'noPortal') {
      pConn.getContainerManager().initializeContainers({ type: 'single' });
    }

    const componentList = [
      'RootContainer',
      'Region',
      'ModalViewContainer',
      'PreviewViewContainer',
      'reference',
      'URL',
      ...additionalComponents
    ];

    await loadComponents(componentList);

    PubSubUtils.publish(publicConstants.PUB_SUB_EVENTS.EVENT_RENDER_APP, {
      props,
      domContainerID,
      componentName: 'RootContainer',
      portalTarget,
      styleSheetTarget
    });
  };

  /**
   * Loads the view under the given container after hitting the view endpoint and getting the view response.
   * @param {string}  viewName - View Name passed in the queryparam
   * @param {string}  portalName - Default portal, used for a fallback when the viewName can not be resolved
   * @param {string}  viewClass - View Name passed in the queryparam
   * @param {string}  containerName - container name
   */
  loadViewByName = (viewName, portalName, viewClass, containerName) => {
    let endPoint = this.getViewRestApi(viewName, viewClass);
    // endpoint is same as ServerURL when the view is requested in a wrong context or if there is any error in fetching the loadView endpoint
    // In that case load the Home for the portal
    if (endPoint === services.getServerURL()) {
      endPoint = this.getHomeRestApi(portalName);
    }
    this.fetchEndPointResource(endPoint, containerName);
  };

  loadPortalView = (portalName, containerName) => {
    const isPortal = true;
    const portalShellEndPoint = this.getHomeRestApi(portalName);
    this.fetchEndPointResource(portalShellEndPoint, containerName, isPortal);
  };

  configureForBrowserBookmark = (payload) => {
    publishForBrowserBookmark(payload);
  };

  checkIfSemanticURL = () => {
    return isSemanticURL();
  };

  /**
   * onPCoreReady API ensures that PCore is loaded and ready to use. This accepts a callback function,
   * that will be called once the application (PCore) infrastructure has established and is ready to perform its initial render.
   *
   * This API registers a callback that will be called once the application infrastructure has established the store and is ready to perform its initial render.
   *
   * @param {Function} callback callback function
   * @example <caption>Usage of onPCoreReady</caption>
   * Usage:
   * const render = function(){
   *  // logic to render and bootstrap your application.
   * };
   * onPCoreReady usage: onPCoreReady((args) => render(args));
   *
   * the callback function with render is called once the infrastructure is ready.
   */
  onPCoreReady = (callback) => {
    PubSubUtils.subscribe(publicConstants.PUB_SUB_EVENTS.EVENT_RENDER_APP, callback, 'renderAppCallback');
  };

  /**
   * onPCoreCleanup API ensures that PCore is properly unmounted. This accepts a callback function,
   * that will be called once the application (PCore) should be unmounted.  This need not be invoked for
   * regular Constellation portal where the React root is the entire page, but is critical for Constellation
   * Web Embeddings.
   *
   * This API registers a callback that will be called when PCore should be unmounted.
   *
   * @param {Function} callback callback function
   * @example <caption>Usage of onPCoreCleanup</caption>
   * Usage:
   * const unmount = function(){
   *  // logic to cleanup application as PCore as Constellation is about to be unmounted
   * };
   * onPCoreCleanup usage: onPCoreCleanup((args) => unmount(args));
   *
   * the callback function with unount is invoked when Constellation should be unmounted.
   */
  onPCoreCleanup = (callback) => {
    PubSubUtils.subscribe(publicConstants.PUB_SUB_EVENTS.EVENT_UNMOUNT_APP, callback, 'unmountAppCallback');
  };

  setFieldsDefaultConfigs = (config) => {
    fieldDefaultUtils.updateFieldDefaults(config);
  };

  setGoogleMapsAPIKey = (googleMapsAPIKey) => {
    services.setGoogleMapsAPIKey(googleMapsAPIKey);
  };

  setFetchDefaultHeaders = (headersObj) => {
    updateHeaders(headersObj);
  };

  setFetchAuthInfo = (authInfo) => {
    updateAuthInfo(authInfo);
  };

  getCaseApi = () => {
    return CaseApis;
  };

  getMashupApi = () => MashupApis;

  enableAppRouting = (enableRouting) => {
    toggleAppRouting(enableRouting);
  };

  initStore = () => configureStore({});

  getBootstrapConfig = async (restServerUrl, tokenInfo) => {
    const authorizationHeader = `${tokenInfo.token_type} ${tokenInfo.access_token}`;

    return fetch(`${restServerUrl}/api/application/v2/data_views/D_pxBootstrapConfig`, {
      method: 'GET',
      headers: new Headers({
        Authorization: authorizationHeader
      })
    }).then((response) => response.json());
  };

  initCoreContainers = (options = {}) => {
    const { containerType = 'single' } = options;
    const rootMashUp = {
      type: 'RootContainer',
      config: {
        renderingMode: 'noPortal'
      },
      children: [
        {
          type: 'ViewContainer',
          config: {
            routingInfo: '@ROUTING_INFO',
            name: 'primary'
          }
        }
      ]
    };

    const rootPConnect = createC11nEnvInstance({
      meta: rootMashUp,
      options: {
        context: 'root',
        pageReference: '',
        target: 'root'
      }
    });
    rootPConnect.getPConnect().getContainerManager().initializeContainers({
      type: containerType,
      name: 'primary',
      context: 'app'
    });

    return {
      props: {
        getPConnect: rootPConnect.getPConnect
      }
    };
  };
}

export default new BootstrapUtils();
